import {computed, inject, Injectable, OnDestroy, Renderer2, RendererFactory2, Signal} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {
  Practice, Invite, PracticeCommentTemplate, CreateCommentTemplate, CommentType, EmailTemplate, UserRepositoryService,
  GenericCode, PracticeProcedure, PatientStatus, AppointmentType, UserSettings, PracticeSettings,
  PracticeUser, PlaceOfService, PracticeReportTemplate, PatientFlagEntity, Letterhead,
  PracticeLocation, PracticePractitioner,
  SessionStorageService, LocalStorageService, ToastService, PermissionProvider
} from '@xmedical/core';
import {AuthEvent} from '@xmedical/core';
import {ApplicationConfig} from '@xmedical/core';
import {config} from '../config/app.config';
import {User} from '@xmedical/core';
import {filter, map} from 'rxjs/operators';
import {CoreEvent, CoreEventsService} from './core-events.service';
import {MasterDataStore, UserStore, TemplatesStore, PracticeStore} from '../state';
import {AuthenticationService} from '../auth/authentication.service';

const SUPERUSER = 'Superuser';

@Injectable({
  providedIn: 'root'
})
export class CoreService extends PermissionProvider implements OnDestroy {

  // Store injections
  private masterDataStore = inject(MasterDataStore);
  private userStore = inject(UserStore);
  private templatesStore = inject(TemplatesStore);
  private practiceStore = inject(PracticeStore);

  // Service injections
  private rendererFactory2 = inject(RendererFactory2);
  private localStorage = inject(LocalStorageService);
  private authService = inject(AuthenticationService);
  private sessionStorage = inject(SessionStorageService);
  private usersRepository = inject(UserRepositoryService);
  private coreEventsService = inject(CoreEventsService);
  private toastService = inject(ToastService);

  // Re-export store signals as readonly for facade pattern
  // Master Data
  readonly patientGenders: Signal<GenericCode[]> = this.masterDataStore.patientGenders;
  readonly patientTitles: Signal<GenericCode[]> = this.masterDataStore.patientTitles;
  readonly patientRaces: Signal<GenericCode[]> = this.masterDataStore.patientRaces;
  readonly patientLanguages: Signal<GenericCode[]> = this.masterDataStore.patientLanguages;
  readonly placesOfService: Signal<PlaceOfService[]> = this.masterDataStore.placesOfService;

  // User Data
  readonly permissions: Signal<string[]> = this.userStore.permissions;
  readonly settings: Signal<UserSettings | null> = this.userStore.settings;
  readonly firstName: Signal<string> = this.userStore.firstName;
  readonly surname: Signal<string> = this.userStore.surname;
  readonly email: Signal<string> = this.userStore.email;

  // Templates
  readonly commentTypes: Signal<CommentType[]> = this.templatesStore.commentTypes;
  readonly commentTemplates: Signal<PracticeCommentTemplate[]> = this.templatesStore.commentTemplates;
  readonly reportTemplates: Signal<PracticeReportTemplate[]> = this.templatesStore.reportTemplates;
  readonly systemReportTemplates = this.templatesStore.systemReportTemplates;
  readonly emailTemplates: Signal<EmailTemplate[]> = this.templatesStore.emailTemplates;

  // Practice Data
  readonly practices: Signal<Practice[]> = this.practiceStore.practices;
  readonly selectedPractice: Signal<Practice | null> = this.practiceStore.selectedPractice;
  readonly selectedPracticeId = this.practiceStore.selectedPracticeId;
  readonly invites: Signal<Invite[]> = this.practiceStore.invites;
  readonly practiceSettings: Signal<PracticeSettings | null> = this.practiceStore.practiceSettings;
  readonly appointmentTypes: Signal<AppointmentType[]> = this.practiceStore.appointmentTypes;
  readonly practiceProcedures: Signal<PracticeProcedure[]> = this.practiceStore.practiceProcedures;
  readonly letterheads: Signal<Letterhead[]> = this.practiceStore.letterheads;
  readonly patientStatuses: Signal<PatientStatus[]> = this.practiceStore.patientStatuses;
  readonly patientFlags: Signal<PatientFlagEntity[]> = this.practiceStore.patientFlags;
  readonly practiceUser: Signal<PracticeUser | null> = this.practiceStore.practiceUser;
  readonly practiceLocations: Signal<PracticeLocation[]> = this.practiceStore.practiceLocations;
  readonly practicePractitioners: Signal<PracticePractitioner[]> = this.practiceStore.practicePractitioners;

  // PermissionProvider implementation
  readonly permissionsLoaded$: Observable<void> = this.coreEventsService.events$.pipe(
    filter(event => event.type === 'permissions_loaded'),
    map(() => void 0)
  );

  // Legacy properties (keep for backwards compatibility)
  notifications = null;
  configuration: ApplicationConfig = null;

  private subscriptions: Subscription[] = [];
  private userVerificationCounter = 0;
  private renderer: Renderer2;
  private visibilityChangedListener = null;
  private onlineListener = null;
  private offlineListener = null;
  private isOffline = false;
  private storesInitialized = false;

  constructor() {
    super();
    this.renderer = this.rendererFactory2.createRenderer(null, null);
    this.subscriptions.push(this.authService.events$
      .pipe(filter(e => e.type === 'login' || e.type === 'logout'))
      .subscribe(event => this.authEventHandler(event)));
    this.configuration = config as ApplicationConfig;
    this.sessionStorage.setItem('app-config', this.configuration);
    this.bindToEvents();

    // Handle page refresh: once OIDC has validated the session, initialize
    // stores if already authenticated. The login event may have been lost
    // because events$ is a Subject and CoreService may not have subscribed
    // before AuthenticationService emitted it.
    this.authService.authReady.then(() => {
      if (this.isAuthenticated && !this.storesInitialized) {
        this.loginProcess();
      }
    });
  }

  get isAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }

  get isSuperuser(): boolean {
    return this.userStore.isSuperuser();
  }

  readonly isReady = computed(() => this.practiceStore.loaded() && this.userStore.loaded() && this.templatesStore.loaded());

  get online(): boolean {
    return this.isOffline === false;
  }

  get hasPermissions(): boolean {
    return this.userStore.hasPermissions();
  }

  get procedureMenuItemLabel(): string {
    const procedures = this.practiceProcedures();
    if (procedures.length !== 1) {
      return 'Procedures';
    }
    return procedures[0].description.length > 0 ? procedures[0].description : 'Procedures';
  }

  get currentUser(): User {
    return this.authService.getUser();
  }

  get practiceEventsChannelName(): string {
    return this.practiceStore.practiceEventsChannelName();
  }

  get practicePatientEventsChannelName(): string {
    return this.practiceStore.practicePatientEventsChannelName();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.unbindFromEvents();
  }

  moduleEnabled(module: string): boolean {
    return this.configuration[module + 'Enabled'];
  }

  setTheme(document: HTMLElement, newTheme: string): void {
    document.setAttribute('class', '');
    switch (newTheme) {
      case 'lightblue':
        document.classList.add('theme-blue-light');
        break;
      case 'blue':
        document.classList.add('theme-blue');
        break;
      case 'dark':
      case 'darkblue':
        document.classList.add('theme-blue-dark');
        break;
      case 'darkorange':
        document.classList.add('theme-orange-dark');
        break;
      case 'orange':
      default:
        break;
    }
  }

  authEventHandler(event: AuthEvent) {
    if (event.type === 'login') {
      this.loginProcess();
    }
    if (event.type === 'logout') {
      this.logoutProcess();
    }
  }

  updateConfig(appConfig: ApplicationConfig) {
    this.configuration = appConfig;
  }

  /* master data logic */

  showToast(message: string, delay = 2500) {
    this.toastService.show({message: message, options: { delay: delay }});
  }

  showSuccessToast(message: string, delay = 1500) {
    this.toastService.show({message: message, options: { classname: 'bg-success text-light', delay: delay }});
  }

  showErrorToast(message: string, delay = 5000) {
    this.toastService.show({message: message, options: { classname: 'bg-danger text-light', delay: delay }});
  }

  // Delegate to stores
  updateUserSettings(settings: UserSettings): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.userStore.updateUserSettings(settings).subscribe({
        next: (result) => resolve(result),
        error: (error) => {
          console.error(error);
          reject(error);
        }
      });
    });
  }

  updatePracticeSettings(settings: PracticeSettings): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.practiceStore.updatePracticeSettings(settings).subscribe({
        next: (result) => resolve(result),
        error: (error) => {
          console.error(error);
          reject(error);
        }
      });
    });
  }

  hasPermission(permission: string): boolean {
    return this.userStore.hasPermission(permission);
  }

  updateCommentTemplate(template: PracticeCommentTemplate): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.templatesStore.updateCommentTemplate(template).subscribe({
        next: (result) => resolve(result),
        error: (error) => {
          console.error(error);
          reject(error);
        }
      });
    });
  }

  createCommentTemplate(template: CreateCommentTemplate): Observable<number> {
    return this.templatesStore.createCommentTemplate(template);
  }

  deleteCommentTemplate(templateId: number): Observable<boolean> {
    return this.templatesStore.deleteCommentTemplate(templateId);
  }

  selectPractice(id: number) {
    this.practiceStore.selectPractice(id);
  }

  readonly hasPractices = computed(() => this.isAuthenticated && this.practiceStore.hasPractices());

  readonly hasInvites = computed(() => this.isAuthenticated && this.practiceStore.hasInvites());

  readonly hasNotifications = computed(() => this.hasInvites());

  /**
   * Ensure user data is loaded (for page refresh scenarios)
   * @returns Promise that resolves when user data is available
   */
  ensureUserLoaded(): Promise<void> {
    return this.userStore.ensureLoaded();
  }

  /**
   * Ensure master data is loaded (for page refresh scenarios)
   * @returns Promise that resolves when master data is available
   */
  ensureMasterDataLoaded(): Promise<void> {
    return this.masterDataStore.ensureLoaded();
  }

  /**
   * Ensure templates are loaded (for page refresh scenarios)
   * @returns Promise that resolves when templates are available
   */
  ensureTemplatesLoaded(): Promise<void> {
    return this.templatesStore.ensureLoaded();
  }

  /**
   * Ensure practice data is loaded (for page refresh scenarios)
   * @returns Promise that resolves when practice data is available
   */
  ensurePracticeLoaded(): Promise<void> {
    return this.practiceStore.ensureLoaded();
  }

  private loginProcess() {
    if (!this.isAuthenticated || this.storesInitialized) {
      return;
    }
    this.storesInitialized = true;

    this.verifyUserExists();

    // Master data has no practice dependency — load in parallel
    this.subscriptions.push(
      this.masterDataStore.initialize().subscribe()
    );

    // Practice store must load first so x-practice-id header is available
    this.subscriptions.push(
      this.practiceStore.initialize().subscribe(() => {
        // Now that a practice is selected, initialize practice-scoped stores
        this.subscriptions.push(
          this.userStore.initialize().subscribe()
        );

        this.subscriptions.push(
          this.templatesStore.initialize().subscribe()
        );
      })
    );
  }

  private logoutProcess() {
    this.storesInitialized = false;

    // Clear all stores
    this.userStore.clearUserData();
    this.practiceStore.clearPracticeData();

    // Clear legacy localStorage items
    this.localStorage.deleteItem('practices');
    this.localStorage.deleteItem('selected_practice');

    // Reset notifications
    this.notifications = [];
  }

  private bindToEvents() {
    this.offlineListener = this.renderer.listen('window', 'offline', (event) => {
      this.isOffline = true;
      this.authService.connectivityChanged(navigator.onLine);
      this.coreEventsService.trigger(new CoreEvent('connection_state_changed'));
    });
    this.onlineListener = this.renderer.listen('window', 'online', (event) => {
      this.isOffline = false;
      this.authService.connectivityChanged(navigator.onLine);
      this.coreEventsService.trigger(new CoreEvent('connection_state_changed'));
    });
    this.visibilityChangedListener = this.renderer.listen('document', 'visibilitychange', (event) => {
      if (document.visibilityState === 'visible') {
        this.authService.appFocusChanged('visible');
      }
    });
  }

  /* invites service logic */

  private unbindFromEvents() {
    if (this.offlineListener) {
      this.offlineListener();
    }
    if (this.onlineListener) {
      this.onlineListener();
    }
    if (this.visibilityChangedListener) {
      this.visibilityChangedListener();
    }
  }

  private verifyUserExists() {
    if (!this.isAuthenticated) {
      return;
    }
    this.subscriptions.push(this.usersRepository.verifySystemUserExists(this.authService.getUser()).subscribe({
      error: (error) => {
        console.error(error);
        this.userVerificationCounter++;
        if (this.userVerificationCounter > 10) {
          this.toastService.show({message: 'Unable to verify user account. Please try refreshing.', options: { classname: 'bg-danger text-light', delay: 5000 }});
        }
      }
    }));
  }
}

